import * as THREE from 'three';

// Camera modes: 0=first person, 1=third person back, 2=third person front
const THIRD_PERSON_DISTANCE = 4;
const THIRD_PERSON_HEIGHT = 1.5;

export class FPSCamera {
  constructor(camera, canvas) {
    this.camera = camera;
    this.yaw = 0;
    this.pitch = 0;
    this.sensitivity = 0.002;
    this.locked = false;

    // Camera mode: 0=first person, 1=third person back, 2=third person front
    this.cameraMode = 0;

    // Player mesh for third person view (set externally)
    this._playerMesh = null;

    this._canvas = canvas;
    canvas.addEventListener('click', () => {
      canvas.requestPointerLock();
    });
    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === canvas;
    });
  }

  /** Set the player mesh group to show/hide based on camera mode */
  setPlayerMesh(mesh) {
    this._playerMesh = mesh;
    if (mesh) mesh.visible = this.cameraMode !== 0;
  }

  /** Cycle camera mode: 0 -> 1 -> 2 -> 0 */
  toggleCameraMode() {
    this.cameraMode = (this.cameraMode + 1) % 3;
    if (this._playerMesh) {
      this._playerMesh.visible = this.cameraMode !== 0;
    }
  }

  update(mouseDX, mouseDY) {
    if (!this.locked) return;

    this.yaw -= mouseDX * this.sensitivity;
    this.pitch -= mouseDY * this.sensitivity;
    this.pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, this.pitch));

    if (this.cameraMode === 0) {
      // First person: camera rotation directly from yaw/pitch
      const euler = new THREE.Euler(this.pitch, this.yaw, 0, 'YXZ');
      this.camera.quaternion.setFromEuler(euler);
    }
    // Third person camera positioning is handled in updateThirdPerson()
  }

  /**
   * Update third-person camera position relative to player eye position.
   * Call this AFTER player.interpolateCamera sets camera.position to eye pos.
   * @param {THREE.Vector3} playerEyePos - The player's eye position
   */
  updateThirdPerson(playerEyePos) {
    if (this.cameraMode === 0) return;

    // Calculate forward direction from yaw and pitch
    const forward = new THREE.Vector3(
      -Math.sin(this.yaw) * Math.cos(this.pitch),
      Math.sin(this.pitch),
      -Math.cos(this.yaw) * Math.cos(this.pitch)
    );

    const up = new THREE.Vector3(0, 1, 0);

    if (this.cameraMode === 1) {
      // Third person BACK: camera behind player
      this.camera.position.copy(playerEyePos)
        .sub(forward.clone().multiplyScalar(THIRD_PERSON_DISTANCE))
        .add(up.clone().multiplyScalar(THIRD_PERSON_HEIGHT));
      this.camera.lookAt(playerEyePos);
    } else if (this.cameraMode === 2) {
      // Third person FRONT: camera in front of player, looking back
      this.camera.position.copy(playerEyePos)
        .add(forward.clone().multiplyScalar(THIRD_PERSON_DISTANCE))
        .add(up.clone().multiplyScalar(THIRD_PERSON_HEIGHT));
      this.camera.lookAt(playerEyePos);
    }

    // Update player mesh position and rotation
    if (this._playerMesh) {
      this._playerMesh.position.copy(playerEyePos);
      this._playerMesh.position.y -= 1.62; // offset to feet (EYE_HEIGHT)
      this._playerMesh.rotation.y = this.yaw + Math.PI; // face forward
    }
  }

  getForward() {
    return new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
  }

  getRight() {
    return new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
  }

  getLookDirection() {
    if (this.cameraMode === 0) {
      const dir = new THREE.Vector3(0, 0, -1);
      dir.applyQuaternion(this.camera.quaternion);
      return dir;
    }
    // In third person, look direction is still based on yaw/pitch (not camera orientation)
    return new THREE.Vector3(
      -Math.sin(this.yaw) * Math.cos(this.pitch),
      Math.sin(this.pitch),
      -Math.cos(this.yaw) * Math.cos(this.pitch)
    ).normalize();
  }

  setSensitivity(value) {
    // Base sensitivity is 0.002; scale by the provided multiplier (default 1.0)
    this.sensitivity = 0.002 * value;
  }
}
